import {
  Component,
  OnDestroy,
  OnInit,
  WritableSignal,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { OfflineBanner } from './shared/components/offline-banner/offline-banner';
import { SplashScreenComponent } from './shared/components/splash-screen/splash-screen';
import { PwaInstallBanner } from './shared/components/pwa-install-banner/pwa-install-banner';
import { PwaUpdateBanner } from './shared/components/pwa-update-banner/pwa-update-banner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ThemeService } from './shared/services/theme.service';
import { MsalService } from '@azure/msal-angular';
import { SignalRWorkerService } from './shared/services/signalr-worker.service';
import { refreshLocationsHandler } from './shared/handlers/refreshLocations.handler';
import { LocationsService } from './shared/services/locations.service';
import { AuthService } from './shared/services/auth.service';
import { PushNotificationService } from './shared/services/push-notification.service';
import { SearchService } from './shared/services/search.service';
import { filter, Subject, takeUntil } from 'rxjs';
import { ErrorLoggerPanel } from './shared/components/error-logger-panel/error-logger-panel';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    OfflineBanner,
    SplashScreenComponent,
    PwaInstallBanner,
    PwaUpdateBanner,
    TranslatePipe,
    ErrorLoggerPanel,
  ],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.scss',
})
export class App implements OnInit, OnDestroy {
  protected title = 'myfruit2.0-mobile';
  private msalService = inject(MsalService);
  private authService = inject(AuthService);
  private signalRService = inject(SignalRWorkerService);
  private locationService = inject(LocationsService);
  private router = inject(Router);
  private pushNotificationService = inject(PushNotificationService);
  private searchService = inject(SearchService);

  online: WritableSignal<boolean> = signal(navigator.onLine);
  showLoader: WritableSignal<boolean> = signal(false);
  showSplashScreen: WritableSignal<boolean> = signal(true);
  showEnablePushCta = this.pushNotificationService.shouldShowCta;

  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);
  private destroy = new Subject<void>();

  constructor() {
    this.themeService.init();
    window.addEventListener('online', () => this.online.set(true));
    window.addEventListener('offline', () => this.online.set(false));

    this.translate.use('en');

    // Fix for Android viewport issues after PWA update reload
    this.fixScrollPositionAfterReload();
  }

  private fixScrollPositionAfterReload(): void {
    // Check if this is a reload after PWA update
    const isPwaReload = sessionStorage.getItem('pwa-update-reload');
    if (isPwaReload) {
      sessionStorage.removeItem('pwa-update-reload');

      // Force scroll to top and reset viewport
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }, 0);
    }
  }
  ngOnInit(): void {
    this.signalRService.listenAll({
      RefreshLocations: (message) =>
        refreshLocationsHandler(message, this.locationService),
    });

    // Clear search when navigation completes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy),
      )
      .subscribe(() => {
        this.searchService.clearSearch();
      });

    this.msalService.instance
      .initialize()
      .then(async () => {
        const response =
          await this.msalService.instance.handleRedirectPromise();
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          console.log(
            '✅ Auth account detected. Continuing post-login initialization...',
          );
          this.msalService.instance.setActiveAccount(accounts[0]);

          // Hide splash screen immediately for authenticated users
          this.showSplashScreen.set(false);

          // Initialize SignalR with authentication token ONLY after user is authenticated
          console.log('🔐 User authenticated, initializing SignalR...');
          await this.runWithTimeout(
            this.signalRService.initializeWithAuth(),
            10000,
            'SignalR initialization',
          );

          // Initialize push notifications AFTER user is authenticated
          console.log(
            '📲 Initializing push notifications for authenticated user...',
          );
          console.log('📍 About to call initializePushNotifications()');
          await this.initializePushNotifications();
          console.log('✅ initializePushNotifications() finished');

          // Load user profile for authenticated users (both after redirect and on page refresh)
          try {
            await this.authService.getUserProfileAsync();
            console.log('✅ User profile loaded successfully');
          } catch (error) {
            console.error('❌ Failed to load user profile:', error);
          }

          // If response exists, user just completed redirect flow - navigate to alarms
          if (response) {
            console.log('✅ Redirect flow completed, navigating to alarms...');
            await this.router.navigate(['alarms']);
          } else if (this.router.url === '/' || this.router.url === '') {
            // If user is already authenticated and on root path, redirect to alarms
            console.log(
              '✅ Authenticated user on root, redirecting to alarms...',
            );
            await this.router.navigate(['alarms']);
          }

          // Hide loader after everything is initialized
          this.showLoader.set(false);
        } else {
          console.warn('⚠️ No user accounts found');
          this.showLoader.set(false);
        }
      })
      .catch((err) => {
        console.error('❌ MSAL initialization error:', err);
        this.showLoader.set(false);
      });

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((reg) => {
          console.log('✅ Service Worker active:', reg);
        })
        .catch((err) => {
          console.error('❌ Service Worker not active:', err);
        });
    }
  }

  private async initializePushNotifications(): Promise<void> {
    console.log('🚀 Starting push notification initialization...');
    const nav = navigator as Navigator & { standalone?: boolean };
    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      nav.standalone === true;
    console.log('📱 Push init diagnostics:', {
      isIos,
      isStandalone,
      userAgent: navigator.userAgent,
      hasServiceWorker: 'serviceWorker' in navigator,
      hasPushManager: 'PushManager' in window,
      notificationPermission:
        'Notification' in window ? Notification.permission : 'unsupported',
    });

    try {
      console.log('📞 Refreshing push subscription status...');
      const isSubscribed = await this.pushNotificationService.refreshStatus();
      console.log('📊 isSubscribed result:', isSubscribed);

      if (!isSubscribed) {
        console.log(
          '📢 Push notifications not active. Waiting for user action.',
        );
      } else {
        console.log('✅ Push notifications already active');
        // Sync existing subscription with backend
        console.log('🔄 Syncing existing subscription with backend...');
        await this.pushNotificationService.syncSubscriptionWithBackend();
        console.log('✅ Sync subscription flow completed');
      }
    } catch (error) {
      console.error('❌ Error initializing push notifications:', error);
      console.error('Error stack:', error);
    } finally {
      console.log('🏁 initializePushNotifications() exit');
    }
  }

  private async runWithTimeout<T>(
    operation: Promise<T>,
    timeoutMs: number,
    operationName: string,
  ): Promise<T | null> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const timeoutPromise = new Promise<null>((resolve) => {
      timeoutId = setTimeout(() => {
        console.warn(
          `⏱️ ${operationName} timed out after ${timeoutMs}ms. Continuing startup flow.`,
        );
        resolve(null);
      }, timeoutMs);
    });

    const result = await Promise.race([operation, timeoutPromise]);

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }

    return result as T | null;
  }

  async enablePushNotificationsFromUserAction(): Promise<void> {
    console.log('👆 User requested enabling push notifications...');
    const success = await this.pushNotificationService.subscribeToPush();

    if (success) {
      console.log('✅ Push notifications enabled successfully');
      return;
    }

    console.log('⚠️ Push notifications were not enabled');
  }

  dismissPushCta(): void {
    this.pushNotificationService.dismissCta();
  }

  ngOnDestroy() {
    this.signalRService.stop();
    this.destroy.next();
    this.destroy.complete();
  }
}
